/**
 * useShipments - Hook para obtener shipments elegibles para facturación
 * 
 * Carga shipments paginados desde GET /invoices/shipments/eligible con infinite scroll.
 * Incluye soporte para filtros avanzados, búsqueda, sorting y estados financieros.
 * Usa React Query con placeholderData para mantener datos previos visibles durante el refetch.
 * Diseñado para trabajar con AG Grid y paginación infinita.
 * 
 * @param {Filters} filters - Filtros aplicados (búsqueda, sorting, financial_status, etc.)
 * @returns {Object} { shipments, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, total }
 */

import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { Filters } from '../models/Filters';
import { ShipmentsService } from '../services/ShipmentsService';

export const useShipments = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ShipmentsApiAdapter(httpClient);
  const service = new ShipmentsService(apiAdapter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['shipments', filters],
    queryFn: ({ pageParam = 1 }) =>
      service.getPaginated({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    // 🎯 En React Query v5, usamos placeholderData para mantener los datos anteriores
    placeholderData: (previousData) => previousData,
  });

  const shipments = data?.pages.flatMap((page) => page.shipments) ?? [];
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    shipments,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    totalPages: data?.pages.length ?? 0,
    totalLocations: shipments.length,
    total: totalFromApi, // Total real del API (con filtros)
  };
};

